/*
 * copy paste from dataengine.js to separate interests for now
 * this is our MEDIATOR
 * We can probably get rid of this as a separate file and consolidate back to just DataEngine.js once we move over fully
*/

import MessageService from 'message-service/message-service';
import DataEvents from '../events/DataEvents';
import DistEvents from '../events/DistEvents';
import HashEvents from '@gameday/core-legacy/lib/enum/HashEvents';
import GumboModel from '../models/GumboModel';
import Timekeeper from './timekeeper/Timekeeper';
import DataFetchService from './services/dataFetchService';
import GameDataPatch from '@gameday/game-data-patch';
import DataCacheService from './services/dataCacheService';

const { CONFIG_CHANGE, SWITCH_EXTERNAL, SWITCH_INTERNAL } = DistEvents;
const { GAME_SWITCH_COMPLETE, UPDATE_GAMEDAY_URL, NEW_VALID_TIMECODE, UPDATE_EXTERNAL, RECOVER_FULL_GUMBO, CONNECTION_ERROR } = DataEvents;
const { GAME_ID_CHANGED } = HashEvents;
const PASSIVE = 'passive';

class GameWorker {
    constructor (_opts, store) {
        this.opts = _opts;
        this.store = store;
        this.previousGamePk = null;
        this.previousGameState = null;
        this.forceUpdate = false;
        this.previousHideScoresSetting = false;
        this.hideScoreSetting = false;
        this.previousAPITimeCode = '';

        const { game } = this.opts;
        this.gamePk = game;

        const {
            datasources,
            env,
            timecode = null,
            site,
            locale,
            language
        } = this.opts;

        this.dataFetchConfig = {
            datasources,
            env,
            timecode,
            site,
            locale,
            language,
            gamePk: this.gamePk,
            secondHelping: false
        };

        if (timecode !== null) {
            this.initTimecode = timecode;
        }

        this.dataFetchService = new DataFetchService(this.dataFetchConfig);
        this.dataPatchService = null;

        this.gameCache = new DataCacheService();
        this.timekeeperConfig = this.buildTimekeeperConfig();
        this.timekeeper = new Timekeeper(this.timekeeperConfig);

        this.setUpMessageListeners();

        this.start();
    }

    /**
     * Function setUpMessageListeners
     *
     * like building a bunch of little cup + string phones
     */
    setUpMessageListeners () {
        // GAME SWITCHING
        MessageService.on(GAME_ID_CHANGED, (payload) => {
            // stop and reset TK
            this.timekeeper.stop();
            this.timekeeper.reset();

            // get new game
            const { game, tfs = null } = payload;
            this.gamePk = game || payload;
            if (tfs !== null) {
                this.initTimecode = tfs;
            }
            this.timekeeperConfig = this.buildTimekeeperConfig();
            this.timekeeper.updateConfig(this.timekeeperConfig);
            const cachedGameData = this.gameCache.getGame(this.gamePk);
            if (cachedGameData) { // need to figure out the mechanics of the cache still. BRB coming soon
                this.applyNewData(cachedGameData);
                // rip timecode from cached data
                // set patch data to cached data
                // diffpatch/secondhelping from start??
            }
            this.dataFetchConfig.gamePk = this.gamePk;
            this.dataFetchConfig.timecode = this.initTimecode;
            this.dataFetchService.updateConfig(this.dataFetchConfig, true);

            this.start();
        });

        // CONFIG CHANGE
        MessageService.on(CONFIG_CHANGE, (config) => {
            // IMPLEMENT WITH SOCKET
        });

        // INIT RENDER READY
        MessageService.one('init_data_loaded', function () {
            MessageService.on('init_data_loaded', function () {
                MessageService.publish(GAME_SWITCH_COMPLETE);
            });
        });

        // NEW TIMECODE FROM TIMEKEEPER
        MessageService.on(NEW_VALID_TIMECODE, ({
            endTimecode,
            pushUpdateId,
            secondHelping = true
        }) => {
            this.dataFetchService.updateParams({
                startTimecode: this.previousAPITimeCode,
                endTimecode,
                secondHelping,
                pushUpdateId
            });
            this.dataFetchService.fetchAPIData()
                .then(result => {
                    MessageService.publish(CONNECTION_ERROR, { error: false });
                    const patched = this.applyNewData(result);
                    if (patched) {
                        this.processUpdates(patched);
                    }
                })
                .catch(error => {
                    console.error('Error fetching API data:', error);
                    MessageService.publish(CONNECTION_ERROR, { error: true });
                });
        });

        // RECOVERY CALL FROM FAILED PATCH/DATA INTEGRITY CHECK
        MessageService.on(RECOVER_FULL_GUMBO, (previousResult = {}) => {
            const { params } = previousResult;

            this.dataFetchService.updateParams({
                /**
                 * If we have an endTimecode from the previous call
                 * use that as the starting point for the new gumbo call.
                 * Otherwise assume we want the "latest" live by supplying
                 * no start or end timecode.
                 * signal that we need a new full data call with `secondHelping: false`
                 */
                startTimecode: null,
                endTimecode: params?.endTimecode ?? null, // DataFetchService will send this as `timecode` for full calls
                secondHelping: false,
                pushUpdateId: null
            });
            this.dataFetchService.fetchAPIData().then(result => {
                const patched = this.applyNewData(result);
                if (patched) {
                    this.processUpdates(patched);
                }
            });
        });

        // SWITCH TO EXTERNAL TK (FROM FV)
        MessageService.on(SWITCH_EXTERNAL, () => {
            this.timekeeper.swapToExternalTimekeeper();
        });

        // SWITCH BACK TO INTERNAL TK
        MessageService.on(SWITCH_INTERNAL, () => {
            this.timekeeper.stop();
            this.dataFetchConfig.timecode = this.previousAPITimeCode;
            this.dataFetchService.updateConfig(this.dataFetchConfig, true);
            this.start();
        });

        // EXTERNAL DATA UPDATE
        MessageService.on(UPDATE_EXTERNAL, (data) => {
            // if we get an update from FV and it contains GUMBO,
            // update it here instead of passing around a pot of soup
            if (!data.timecode) {
                const patched = this.applyNewData({ data, newData: true });
                if (patched) {
                    this.processUpdates(patched);
                }
            }
        });
    }

    /**
     * Function buildTimekeeperConfig
     *
     * @return {object} configuration for starting timekeeper.
     *
     * builds config based on init params
     */
    buildTimekeeperConfig () {
        const {
            config: {
                disableWebSockets,
                forceWebSockets,
                webSocketWindow,
                noDelayWebSockets,
                env
            },
            dataMode
        } = this.opts;

        return {
            gameInfo: {
                gamePk: this.gamePk
            },
            websocketConfig: {
                disableWebSockets,
                webSocketWindow,
                forceWebSockets,
                noDelayWebSockets,
                env
            },
            initTimecode: this.initTimecode,
            dataMode
        };
    }

    /**
     * Function updateTimekeeperConfig
     * @param gumboData {object}
     *
     * @return {object} updated configuration for starting timekeeper.
     *
     * updates config based on initial data call
     */
    updateTimekeeperConfig (gumboData) {
        const {
            datetime: {
                dateTime
            },
            status: {
                abstractGameCode
            }
        } = gumboData;
        const updatedConfig = {
            ...this.timekeeperConfig,
            gameInfo: {
                ...this.timekeeperConfig.gameInfo,
                abstractGameCode, // game state from GUMBO
                dateTime // game start time
            }
        };
        this.timekeeperConfig = updatedConfig;
        this.timekeeper.updateConfig(this.timekeeperConfig);
    }

    /**
     * Function start
     *
     * kick off Gameday!
     */
    start () {
        const { initTimecode, dataMode } = this.timekeeperConfig;
        const prefetch = (dataMode !== PASSIVE || initTimecode);
        if (prefetch) {
            this.startWithPrefetch();
        } else {
            this.timekeeper.startTimekeeping();
        }
    }

    /**
     * Function startWithPrefetch
     *
     *
     * make initial API call, update TK config, start TK, process everything
     */
    startWithPrefetch () {
        this.dataFetchService.fetchAPIData().then(result => {
            const gameData = result.data.gameData;
            this.updateTimekeeperConfig(gameData);

            const timecode = result.data.metaData.timeStamp;
            this.timekeeper.startTimekeeping(timecode);

            const patched = this.applyNewData(result);
            this.processUpdates(patched);
            MessageService.publish('init_data_loaded');
        });
    }

    /**
     * Function applyNewData
     * @param result {object}
     *
     * @return {object || boolean} new data || success of applying data
     *
     * handle merging update into existing data
     */
    applyNewData (result) {
        const { data } = result;
        const isPatch = data?.isPatch === true;
        let isSuccess = true;

        this.patchQC = true;

        if (!isPatch && !data?.metaData) {
            // bad data, start over please
            this.patchQC = false;
            isSuccess = false;
        }
        if (!isPatch) {
            isSuccess = this.setAllData(result);
        } else if (isPatch && data?.diffs.length > 0) {
            isSuccess = this.applyPatch(result);
        } else {
            isSuccess = false;
        }

        if (!isSuccess && this.patchQC === false) {
            /**
             * Patch has failed, reissue the timecode update
             * minus and endTimecode, and signal that we need a new
             * full data call with `secondHelping: false`
             * NOTE: Unsure if this is the correct approach, or
             *       shoud we be looking to call an synchonous internal method.
             *       Handling this via message feels inline with the data flow
             *       patterns, but work discussing.
             */
            MessageService.publish(RECOVER_FULL_GUMBO, result);
        }

        return isSuccess;
    }

    /**
     * Function applyPatch
     * @param result {object}
     *
     * @return {object} patched data
     *
     * patch diff(s) into existing data
     */
    applyPatch (result) {
        let tempData;

        result.data.diffs.forEach((diff) => {
            // If it's an array of diffs, patch those diffs onto gumbo object
            tempData = this.dataPatchService.patch(diff);
        });
        this.patchQC = tempData.success;

        if (this.patchQC) {
            const remodel = new GumboModel(tempData.data, result.params);

            return {
                data: remodel,
                newData: true
            };
        } else {
            return false;
        }
    }

    /**
     * Function setAllData
     * @param result {object}
     *
     * @return {object} data
     *
     * blast away the old data and speak the new truth
     */
    setAllData (result) {
        if (this.dataPatchService) {
            this.dataPatchService.destroy();
        }
        this.dataPatchService = new GameDataPatch(result.data.dataString);
        return {
            data: result.data,
            newData: true
        };
    }

    /**
     * Function processUpdates
     * @param updatedData {object}
     *
     * check if things changed, update the store, update the checks so we know if things change again
     */
    processUpdates (updatedData = { data: {}, newData: false }) {
        const params = this.opts.views;
        const { data, newData } = updatedData;

        if (data) {
            const isGameIDChanged = (this.previousGamePk !== data.gamePk && this.previousGamePk !== null);
            const apiGameState = data.gameData.status.abstractGameState;
            const isGamestateChanged = (this.forceUpdate === true);
            const hasNewData = (newData === true);
            const isHideScoresChanged = (this.previousHideScoresSetting !== this.hideScoreSetting);
            // Save this for later
            if (hasNewData || isGameIDChanged || isGamestateChanged || isHideScoresChanged) {
                this.store({
                    success: true,
                    data,
                    source: hasNewData ? 'server' : 'cache',
                    params,
                    dataSourceName: 'gumbo'
                });

                // Always pass game info to changeView and let that handle any changes necessary
                MessageService.publish(UPDATE_GAMEDAY_URL, {
                    data: {
                        prev_game_state: this.previousGameState,
                        next_game_state: apiGameState,
                        game_updated: isGameIDChanged
                    }
                });

                this.previousGamePk = data.gamePk;
                this.previousGameState = apiGameState;
                this.previousAPITimeCode = data.metaData.timeStamp;
            }
        } else {
            // No new updates
        }
    }
}

export default GameWorker;
